import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, ChevronDown, ChefHat, Plus, Heart } from "lucide-react";
import BrandLockup from "@/components/BrandLockup";
import HeaderUtils from "@/components/HeaderUtils";
import PrimaryNav from "@/components/PrimaryNav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RecipeSubmitDialog from "@/components/RecipeSubmitDialog";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { RECIPES, RECIPE_MEALS } from "@/lib/recipesData";

const MEAL_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" };
const notEmpty = (v) => v != null && v !== "";
const hasMacros = (r) => [r.protein, r.carb, r.fat].some(notEmpty);
const photoUrl = (path) => `${API}/community-recipes/photo/${path}?auth=${localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token") || ""}`;

function RecipeCard({ r, highlight = false }) {
  const [open, setOpen] = useState(highlight);
  const community = r.source === "community";
  const [voted, setVoted] = useState(!!r.voted);
  const [count, setCount] = useState(r.helpful || 0);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  // When a recipe arrives with ?highlight=<id> from Home's Recipe of the Week,
  // scroll it into view and pulse a yellow ring so the athlete sees exactly
  // what they tapped on. The card also auto-opens so ingredients/method are
  // visible without an extra click.
  useEffect(() => {
    if (!highlight || !cardRef.current) return;
    const t = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => clearTimeout(t);
  }, [highlight]);

  const vote = async () => {
    if (busy) return;
    setBusy(true);
    const nv = !voted;
    setVoted(nv);
    setCount((c) => Math.max(0, c + (nv ? 1 : -1)));
    try {
      const { data } = await api.post(`/community-recipes/${r.id}/helpful`);
      setVoted(data.voted);
      setCount(data.helpful);
    } catch (e) {
      setVoted(!nv);
      setCount((c) => Math.max(0, c + (nv ? -1 : 1)));
      toast.error("Couldn't save your vote — please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={cardRef}
      className={`border bg-[#141414] transition-shadow ${highlight ? "border-[#E2E30A] recipe-highlight-pulse" : "border-white/10"}`}
      data-testid={`recipe-item-${r.id}`}>
      <div className="flex items-stretch">
        <button type="button" data-testid={`recipe-toggle-${r.id}`} onClick={() => setOpen((o) => !o)}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors">
          {community && r.photo && (
            <img src={photoUrl(r.photo)} alt="" loading="lazy" className="h-11 w-11 object-cover border border-white/10 shrink-0" />
          )}
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white truncate">{r.name}</span>
              {community && <span className="text-[8px] font-bold uppercase tracking-wider text-[#E2E30A] border border-[#E2E30A]/40 px-1.5 py-0.5 shrink-0" data-testid={`recipe-usercontrib-${r.id}`}>User contribution</span>}
            </span>
            <span className="block text-[11px] text-zinc-400">{MEAL_LABEL[r.meal] || r.meal}{(r.tags || []).length ? " · " + r.tags.join(" · ") : ""}</span>
          </span>
          {notEmpty(r.kcal) && <span className="text-[10px] font-mono text-[#E2E30A] shrink-0">{r.kcal} kcal</span>}
          <ChevronDown className={`h-4 w-4 text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {community && (
          <button type="button" data-testid={`recipe-helpful-${r.id}`} onClick={vote} disabled={busy} title="Mark as helpful"
            className={`shrink-0 flex flex-col items-center justify-center gap-0.5 px-3.5 border-l border-white/10 transition-colors ${voted ? "text-[#FF3B30]" : "text-zinc-400 hover:text-[#FF3B30]"}`}>
            <Heart className={`h-4 w-4 ${voted ? "fill-current" : ""}`} />
            <span className="text-[10px] font-mono" data-testid={`recipe-helpful-count-${r.id}`}>{count}</span>
          </button>
        )}
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
          {community && r.photo && (
            <img src={photoUrl(r.photo)} alt={r.name} loading="lazy" className="w-full max-h-64 object-cover border border-white/10" />
          )}
          {hasMacros(r) && (
            <div className="flex gap-3 text-[11px] font-mono">
              {notEmpty(r.protein) && <span className="text-[#4C9AFF]">P {r.protein}g</span>}
              {notEmpty(r.carb) && <span className="text-[#34C759]">C {r.carb}g</span>}
              {notEmpty(r.fat) && <span className="text-[#FF9500]">F {r.fat}g</span>}
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4C9AFF]">Ingredients</p>
            <ul className="text-sm text-zinc-200 mt-1 list-disc pl-5 space-y-0.5">
              {(r.ingredients || []).map((x, idx) => <li key={`${idx}-${x}`}>{x}</li>)}
            </ul>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A]">Method</p>
            <ol className="text-sm text-zinc-200 mt-1 list-decimal pl-5 space-y-0.5">
              {(r.method || []).map((x, idx) => <li key={`${idx}-${x.slice(0, 20)}`}>{x}</li>)}
            </ol>
          </div>
          {community && <p className="text-[10px] text-zinc-300 pt-1">Shared by a member of the community.</p>}
        </div>
      )}
    </div>
  );
}

export default function Recipes() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const [q, setQ] = useState("");
  const [meal, setMeal] = useState("all");
  const [tag, setTag] = useState("All");
  const [community, setCommunity] = useState([]);
  const [submitOpen, setSubmitOpen] = useState(false);

  const loadCommunity = useCallback(async () => {
    try { const { data } = await api.get("/community-recipes"); setCommunity(data.recipes || []); }
    catch (e) { console.error("Recipes: load community recipes failed", e); }
  }, []);
  useEffect(() => { loadCommunity(); }, [loadCommunity]);

  // Community submissions surface first (already sorted by "helpful" server-side).
  const all = useMemo(() => [...community, ...RECIPES], [community]);

  const allTags = useMemo(
    () => ["All", ...Array.from(new Set(all.flatMap((r) => r.tags || []))).sort()],
    [all]
  );

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return all.filter((r) => {
      if (meal !== "all" && r.meal !== meal) return false;
      if (tag !== "All" && !(r.tags || []).includes(tag)) return false;
      if (!term) return true;
      return (r.name + " " + r.meal + " " + (r.tags || []).join(" ") + " " + (r.ingredients || []).join(" ")).toLowerCase().includes(term);
    });
  }, [q, meal, tag, all]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BrandLockup />
          <div className="ml-auto shrink-0"><HeaderUtils /></div>
        </div>
        <PrimaryNav />
      </header>

      <main data-testid="recipes-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="grid place-items-center h-11 w-11 bg-[#FF9500] shrink-0"><ChefHat className="h-5 w-5 text-black" /></div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-none">Recipes</h1>
            <p className="text-sm text-zinc-300 mt-1.5">Simple, balanced meals and snacks for everyone — with ingredients, method and macros where we have them. Filter by meal or dietary fit.</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4 border border-[#E2E30A]/25 bg-[#E2E30A]/[0.04] px-4 py-3">
          <p className="text-sm text-zinc-200">Got a recipe you love? <span className="text-zinc-400">Share it — approved ideas appear as a “User contribution”.</span></p>
          <Button data-testid="recipes-submit-btn" onClick={() => setSubmitOpen(true)}
            className="shrink-0 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-bold uppercase tracking-wider text-xs">
            <Plus className="h-4 w-4 mr-1.5" /> Share a recipe
          </Button>
        </div>

        <div className="relative mb-3">
          <Search className="h-4 w-4 text-zinc-300 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="recipes-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search e.g. chicken, oats, vegan…" className="pl-9 bg-black border-white/15 text-white" />
        </div>

        <div className="flex flex-wrap gap-1.5 mb-2">
          {RECIPE_MEALS.map((m) => (
            <button key={m.k} data-testid={`recipes-meal-${m.k}`} onClick={() => setMeal(m.k)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider border transition-colors ${meal === m.k ? "border-[#FF9500] text-[#FF9500]" : "border-white/15 text-zinc-300 hover:text-white"}`}>{m.label}</button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-5">
          {allTags.map((t) => (
            <button key={t} data-testid={`recipes-tag-${t}`} onClick={() => setTag(t)}
              className={`px-2.5 py-1 text-[11px] uppercase tracking-wider border transition-colors ${tag === t ? "border-[#E2E30A] text-[#E2E30A]" : "border-white/15 text-zinc-300 hover:text-white"}`}>{t}</button>
          ))}
        </div>

        <p className="text-[11px] uppercase tracking-wider text-zinc-300 mb-3" data-testid="recipes-count">{list.length} recipe{list.length === 1 ? "" : "s"}</p>

        <div className="space-y-2">
          {list.length === 0 ? (
            <p data-testid="recipes-empty" className="text-sm text-zinc-400 py-8 text-center">No recipes match that — try another filter or word.</p>
          ) : list.map((r) => <RecipeCard key={r.id} r={r} highlight={r.id === highlightId} />)}
        </div>
      </main>

      <RecipeSubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} onSubmitted={loadCommunity} />
    </div>
  );
}
